import { toDateOnlyString } from './dateOnly';

const BATCH_DRAFT_PREFIX = 'billing:transaction-batch:v1';
const SINGLE_DRAFT_PREFIX = 'billing:transaction-single:v1';
const DB_NAME = 'billing-transaction-drafts';
const STORE_NAME = 'attachments';

export const createBatchId = () => (
  window.crypto?.randomUUID?.()
  || `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

export const getBatchDraftKey = (user) => (
  user?._id ? `${BATCH_DRAFT_PREFIX}:${user._id}:${user.role || 'unknown'}` : ''
);

export const getSingleTransactionDraftKey = (user) => (
  user?._id ? `${SINGLE_DRAFT_PREFIX}:${user._id}:${user.role || 'unknown'}` : ''
);

export const normalizeBatchItem = (item) => ({
  ...item,
  date: toDateOnlyString(item.date),
  amount: Number(item.amount) || 0,
  newFiles: Array.isArray(item.newFiles) ? item.newFiles : [],
});

export const validateBatchItem = (item) => {
  const fields = [];
  if (!item.customerId) fields.push('customerId');
  if (!item.supplierId) fields.push('supplierId');
  if (!item.transactionType) fields.push('transactionType');
  if (!toDateOnlyString(item.date)) fields.push('date');
  if (!Number.isFinite(Number(item.amount)) || Number(item.amount) <= 0) fields.push('amount');
  return { valid: fields.length === 0, fields };
};

export const getBatchFingerprint = (item) => [
  item.customerId,
  item.supplierId,
  item.transactionType,
  Number(item.amount),
  String(item.document || '').trim().toLowerCase(),
  toDateOnlyString(item.date),
].join('|');

export const validateBatch = (items) => {
  const fingerprints = new Map();
  return items.map((item, index) => {
    const validation = validateBatchItem(item);
    const fingerprint = getBatchFingerprint(item);
    const duplicateOf = fingerprints.has(fingerprint) ? fingerprints.get(fingerprint) : null;
    if (!fingerprints.has(fingerprint)) fingerprints.set(fingerprint, index);
    return {
      ...validation,
      valid: validation.valid && duplicateOf === null,
      duplicateOf,
    };
  });
};

export const summarizeBatch = (items) => {
  const validations = validateBatch(items);
  const byType = {};
  const bySupplier = {};
  let total = 0;
  items.forEach((item) => {
    const amount = Number(item.amount) || 0;
    total += amount;
    byType[item.transactionType || 'incomplete'] = (byType[item.transactionType || 'incomplete'] || 0) + amount;
    if (item.supplierId) bySupplier[item.supplierId] = (bySupplier[item.supplierId] || 0) + amount;
  });
  const valid = validations.filter((item) => item.valid).length;
  return { total, byType, bySupplier, count: items.length, valid, invalid: items.length - valid, validations };
};

export const upsertBatchItem = (items, item, editingId = null) => (
  editingId
    ? items.map((current) => current.id === editingId ? item : current)
    : [...items, item]
);

export const removeBatchItem = (items, id) => items.filter((item) => item.id !== id);

export const duplicateBatchItem = (item, id = createBatchId()) => ({
  ...item,
  id,
  document: '',
  newFiles: [],
  serverError: '',
});

const openDraftDb = () => new Promise((resolve, reject) => {
  if (!window.indexedDB) return resolve(null);
  const request = window.indexedDB.open(DB_NAME, 1);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME);
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const writeFiles = async (key, items, editorFiles) => {
  const db = await openDraftDb();
  if (!db) return;
  await new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put({
      items: Object.fromEntries(items.map((item) => [item.id, item.newFiles || []])),
      editor: editorFiles || [],
    }, key);
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
  });
  db.close();
};

const readFiles = async (key) => {
  const db = await openDraftDb();
  if (!db) return { items: {}, editor: [] };
  const result = await new Promise((resolve, reject) => {
    const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result || { items: {}, editor: [] });
    request.onerror = () => reject(request.error);
  });
  db.close();
  return result;
};

export const saveBatchDraft = async (key, draft) => {
  if (!key) return;
  const serializableItems = draft.items.map(({ newFiles, ...item }) => ({
    ...item,
    attachmentNames: (newFiles || []).map((file) => file.name),
  }));
  const { newFiles: editorFiles, ...editor } = draft.editor;
  window.localStorage.setItem(key, JSON.stringify({
    version: 1,
    savedAt: new Date().toISOString(),
    idempotencyKey: draft.idempotencyKey,
    items: serializableItems,
    editor: {
      ...editor,
      attachmentNames: (editorFiles || []).map((file) => file.name),
    },
  }));
  await writeFiles(key, draft.items, editorFiles);
};

export const loadBatchDraft = async (key) => {
  if (!key) return null;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    const draft = JSON.parse(raw);
    if (draft.version !== 1 || !Array.isArray(draft.items)) return null;
    const files = await readFiles(key).catch(() => ({ items: {}, editor: [] }));
    return {
      ...draft,
      items: draft.items.map((item) => ({ ...item, newFiles: files.items?.[item.id] || [] })),
      editor: { ...draft.editor, newFiles: files.editor || [] },
    };
  } catch (_error) {
    return null;
  }
};

export const clearBatchDraft = async (key) => {
  if (!key) return;
  window.localStorage.removeItem(key);
  const db = await openDraftDb();
  if (!db) return;
  await new Promise((resolve, reject) => {
    const request = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(key);
    request.onsuccess = resolve;
    request.onerror = () => reject(request.error);
  });
  db.close();
};

export const saveSingleTransactionDraft = (key, editor) => saveBatchDraft(key, {
  idempotencyKey: '',
  items: [],
  editor,
});

export const loadSingleTransactionDraft = async (key) => {
  const draft = await loadBatchDraft(key);
  return draft?.editor || null;
};

export const clearSingleTransactionDraft = clearBatchDraft;
